import classNames from "classnames";
import Masonry from "react-responsive-masonry";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { BaseImage } from "../../../components/Content/BaseImage.tsx";
import { Icon, type IconName } from "../../../components/Icon/Icon.tsx";
import { useAppMutation } from "../../../http/useAppMutation.ts";
import { useAppQuery } from "../../../http/useAppQuery.ts";
import { trackEvent } from "../../../tracking/tracking.ts";
import type { DetailedImage } from "../../Board/types.ts";
import { BOARD_STATE_BY_PRESET } from "../../boardState/presets.ts";
import type { StatePreset } from "../../boardState/types.ts";
import { useRedirectToBoard } from "../../hooks/useRedirectToBoard.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import type { ModelWithIsCompatibleAndTags } from "../../types.ts";
import { useCreateBoard } from "../HomeIndex/hooks/useCreateBoard.ts";
import { quickActionsDialogStore } from "./stores/quickActionsDialogStore.ts";

export type ChildrenProp =
  | {
      iconName: IconName;
    }
  | {
      imageUrl: string;
      contains?: boolean;
    }
  | {
      leftImageUrl: string;
      rightImageUrl: string;
    }
  | {
      imageUrls: string[];
      columnsCount: number;
    };

export const QuickActionsButton = ({
  preset,
  label,
  subLabel,
  createAssetFromContentUuid,
  childrenProp,
  models,
}: {
  preset: StatePreset;
  label: string;
  subLabel?: string;
  createAssetFromContentUuid?: string;
  childrenProp: ChildrenProp;
  models: ModelWithIsCompatibleAndTags[];
}) => {
  const workspaceUuid = useWorkspaceUuid();
  const redirectToBoard = useRedirectToBoard();
  const { mutate: createAsset, isLoading: isCreateAssetLoading } =
    useAppMutation({
      path: "assets/create",
      invalidate: [`workspaces/${workspaceUuid}/boards`],
    }).mutation;

  // XXX: If we know the content does not exist in DB, we don't display the button to avoid broken behaviors.
  // FIXME: We need a way to make sure that the content is an image
  // not a video. We could either create a new route images/uuid
  // or add a query param to the current route.
  const { data: content } = useAppQuery<DetailedImage>({
    queryKey: createAssetFromContentUuid
      ? `contents/${createAssetFromContentUuid}`
      : null,
  });

  // FIXME: This is a temporary fix to avoid displaying quick actions for which the models used by the preset are not available.
  // This could happen when models are deleted or removed from the library. Ideally presets should be
  // stored in the backend, and retrieved through a specific query.
  const areModelsValid =
    BOARD_STATE_BY_PRESET[preset].modelUuids?.every((modelUuid) =>
      models.some((model) => model.uuid === modelUuid),
    ) ?? false;

  const { createBoard, isCreateBoardLoading } = useCreateBoard({
    onSuccess: (uuid) => {
      if (createAssetFromContentUuid) {
        createAsset(
          {
            board_uuid: uuid,
            content_uuid: createAssetFromContentUuid,
          },
          {
            onSuccess: ({ data }: { data: { uuid: string } }) => {
              redirectToBoard({
                uuid,
                boardStatePreset: preset,
                baseBoardState: { selectedAssetUuid: data.uuid },
              });
              quickActionsDialogStore.close();
            },
          },
        );
      } else {
        redirectToBoard({
          uuid,
          boardStatePreset: preset,
        });
        quickActionsDialogStore.close();
      }
    },
  });

  return (
    (content !== undefined || !createAssetFromContentUuid) &&
    areModelsValid && (
      <div className="flex-col w-[194px] gap-200">
        <BaseButton
          className="h-[160px] flex-col-center rounded-300 bg-surface-secondary-rest hover:bg-surface-secondary-hover p-200"
          onClick={() => {
            trackEvent("quick_actions:select", {
              preset,
              category: quickActionsDialogStore.getState().selectedCategory,
            });
            createBoard();
          }}
          loading={isCreateBoardLoading || isCreateAssetLoading}
        >
          {"imageUrl" in childrenProp && (
            <BaseImage
              imageClassName={classNames(
                "w-full h-full rounded-300",
                childrenProp.contains ? "object-contain" : "object-cover",
              )}
              className="w-full h-full"
              src={childrenProp.imageUrl}
            />
          )}
          {"leftImageUrl" in childrenProp &&
            "rightImageUrl" in childrenProp && (
              <div className="relative w-full h-full overflow-clip rounded-300">
                <div
                  className="absolute inset-0 w-full h-full overflow-clip"
                  style={{
                    transform: "translateX(-50%)",
                  }}
                >
                  <BaseImage
                    imageClassName="w-full h-full object-cover rounded-300 rounded-r-300 rounded-y-300"
                    className="w-full h-full"
                    src={childrenProp.leftImageUrl}
                    style={{
                      transform: "translateX(50%)",
                    }}
                  />
                </div>
                <div
                  className="absolute inset-0 w-full h-full overflow-clip"
                  style={{
                    transform: "translateX(50%)",
                  }}
                >
                  <BaseImage
                    imageClassName="w-full h-full object-cover rounded-l-300 rounded-y-300"
                    className="w-full h-full"
                    src={childrenProp.rightImageUrl}
                    style={{
                      transform: "translateX(-50%)",
                    }}
                  />
                </div>
              </div>
            )}
          {"imageUrls" in childrenProp && (
            <div className="w-full h-full overflow-clip rounded-300">
              <Masonry columnsCount={childrenProp.columnsCount} gutter="0px">
                {childrenProp.imageUrls.map((imageUrl) => (
                  <BaseImage key={imageUrl} src={imageUrl} />
                ))}
              </Masonry>
            </div>
          )}
          {"iconName" in childrenProp && <Icon name={childrenProp.iconName} />}
        </BaseButton>
        <div className="flex-col text-wrap">
          <div className="min-h-500 label-md-semibold">{label}</div>
          <div className="min-h-500 label-sm-default text-secondary">
            {subLabel}
          </div>
        </div>
      </div>
    )
  );
};
